import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setError,
  setLoading,
  setSegments,
  setSegmentCategoryName, // Import the new action
} from "../../../../state/reducers/communicationSlice"; // Import Redux actions
import {
  getSegments,
  importContacts,
} from "../../../../services/communication.services";
import * as XLSX from "xlsx"; // Import the xlsx library
import toast from "react-hot-toast";
import { CheckCircle, X, Search } from "lucide-react";

const UploadConfirmation = ({
  fetchContacts,
  excelFile, // The raw Excel file passed from UploadExcelFile
  fileName,
  onClose,
  onConfirm,
  channel,
  handleCloseModal,
}) => {
  const dispatch = useDispatch();
  const { segments, segmentCategoryName } = useSelector(
    (state) => state.communication
  );
  const [activeTab, setActiveTab] = useState("create"); // Handle tab switching
  const [segmentName, setSegmentName] = useState(""); // Segment name input
  const [selectedSegment, setSelectedSegment] = useState(null); // Track selected segment
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  // Existing contacts from Redux
  const existing = useSelector((state) => state.communication.contacts) || [];

  const fetchSegments = async () => {
    dispatch(setLoading(true)); // Set loading state to true
    try {
      const response = await getSegments(); // Fetch data from the backend
      if (response && response.segment_counts) {
        dispatch(setSegments(response.segment_counts)); // Set segments to Redux
      } else {
        dispatch(setError("Unexpected response structure from the server."));
      }
    } catch (err) {
      dispatch(setError(err.message || "Failed to fetch segments.")); // Set error in Redux
    } finally {
      dispatch(setLoading(false)); // Set loading state to false
    }
  };

  const filteredSegments = segments.filter((segment) =>
    segment.segment__name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleImportFromExcel = async () => {
    if (!fileName || (!segmentName && !selectedSegment)) {
      setError("Please fill in all the fields and select a file.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const reader = new FileReader();

      reader.onload = async (e) => {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonContacts = XLSX.utils.sheet_to_json(worksheet);

        const newContacts = jsonContacts.map((row) => ({
          email: (row.Email || "").toLowerCase().trim(),
          phone_number: String(
            row["Phone"] || row["Phone Number"] || row["phone"] || ""
          ).trim(),
        }));
        // ✅ Duplicate validation only in "select" mode
        if (activeTab === "select" && selectedSegment) {
          const segmentIdToCheck = selectedSegment?.segment__id;
          const existingSegmentContacts = existing.filter(
            (c) => c.segment === segmentIdToCheck
          );

          const existingIndex = existingSegmentContacts.map(
            (c) => `${c.email?.toLowerCase()}-${c.phone_number}`
          );

          const allDuplicates = newContacts.every((c) =>
            existingIndex.includes(`${c.email}-${c.phone_number}`)
          );

          if (allDuplicates) {
            toast.error("All contacts in the uploaded file already exist.");
            setIsLoading(false);
            return;
          }
        }

        // Prepare formData
        const formData = new FormData();
        formData.append("excel_file", excelFile);

        if (activeTab === "create") {
          formData.append("segment_name", segmentName);
          formData.append("segment_category_name", segmentCategoryName);
        } else if (activeTab === "select" && selectedSegment) {
          formData.append("segment_name", selectedSegment.segment__name);
          formData.append(
            "segment_category_name",
            selectedSegment.segment__category__name
          );
        }

        // Import contacts
        const response = await importContacts(formData);

        // Update segment contact count
        if (response && selectedSegment) {
          const updatedSegments = segments.map((segment) =>
            segment.id === selectedSegment.id
              ? {
                  ...segment,
                  total_contacts: segment.total_contacts + jsonContacts.length,
                }
              : segment
          );

          dispatch(setSegments(updatedSegments));
          setSelectedSegment(
            updatedSegments.find((seg) => seg.id === selectedSegment.id)
          );
        }

        toast.success(response.message || "Contacts imported successfully!");
        fetchSegments();

        fetchContacts();
        onClose();
        handleCloseModal();
      };
      reader.readAsBinaryString(excelFile);
    } catch (err) {
      console.error("Error:", err.message);
      setError(err.message || "Failed to import contacts.");
      toast.error(err.message || "Failed to import contacts.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white w-full max-w-sm sm:max-w-md lg:max-w-lg xl:max-w-2xl rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-lg">
          <h2 className="text-base sm:text-lg font-bold text-gray-800">
            Upload Confirmation
          </h2>
          <button
            className="text-gray-500 hover:text-gray-800 text-xl p-1 hover:bg-gray-100 rounded-full transition-colors"
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {/* Success Message */}
          <div className="text-green-500 mb-4 sm:mb-6">
            <p className="text-sm sm:text-base">
              <strong className="break-all">{fileName}</strong> imported
              successfully.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex border-b mb-4 sm:mb-6">
            <button
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm sm:text-base transition-colors ${
                activeTab === "create"
                  ? "text-blue-500 border-b-2 border-blue-500"
                  : "text-gray-600 hover:text-blue-500"
              }`}
              onClick={() => setActiveTab("create")}
            >
              Create a Segment
            </button>
            <button
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm sm:text-base transition-colors ${
                activeTab === "select"
                  ? "text-blue-500 border-b-2 border-blue-500"
                  : "text-gray-600 hover:text-blue-500"
              }`}
              onClick={() => setActiveTab("select")}
            >
              Select a Segment
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === "create" && (
            <div className="space-y-4">
              {/* List Name Input */}
              <div>
                <label className="block text-gray-700 mb-2 text-sm sm:text-base font-medium">
                  List Name
                </label>
                <input
                  type="text"
                  value={segmentName}
                  onChange={(e) => setSegmentName(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  placeholder="Enter list name"
                />
              </div>

              {/* Segment Category Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Segment Category Name
                </label>
                <select
                  value={segmentCategoryName}
                  onChange={(e) => {
                    setSegmentCategoryName(e.target.value); // Update the local state
                    dispatch(setSegmentCategoryName(e.target.value)); // Dispatch to Redux
                  }}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm sm:text-base"
                  required
                >
                  <option value="">Select Category</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="email">Email</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === "select" && (
            <div className="space-y-4">
              <p className="text-xs sm:text-sm text-gray-600">
                Select a segment from existing options:
              </p>

              {/* Search box */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search size={16} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  className="w-full pl-10 pr-3 py-2 sm:py-3 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  placeholder="Search segments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Segments grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-48 sm:max-h-60 overflow-y-auto pr-1">
                {filteredSegments.map((segment) => (
                  <div
                    key={segment.segment__id}
                    onClick={() => setSelectedSegment(segment)}
                    className={`border rounded-md p-3 cursor-pointer transition-all ${
                      selectedSegment?.segment__id === segment.segment__id
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-blue-200 hover:bg-gray-50"
                    }`}
                  >
                    <p className="font-medium text-gray-800 truncate text-sm sm:text-base">
                      {segment.segment__name}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500">
                      Contacts: {segment.total_contacts}
                    </p>
                  </div>
                ))}
              </div>

              {filteredSegments.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">
                    {searchQuery
                      ? "No segments found matching your search."
                      : "No segments available."}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Confirm Button */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <button
              className={`w-full sm:w-auto px-6 py-2.5 sm:py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm sm:text-base font-medium ${
                isLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              onClick={handleImportFromExcel}
              disabled={isLoading}
            >
              {isLoading ? "Importing..." : "Import"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadConfirmation;
